// ─────────────────────────────────────────────────────────────────────────
//  luxe.js — единый набор дизайн-токенов «Dark Luxe Gold».
//
//  Это «рычаг дизайна» для всего приложения: цвета, шрифт, готовые объекты
//  стилей (карточки, чипсы, надзаголовок, золотая линия, кнопки) и хелперы
//  анимаций. Чтобы перекрасить/переоформить экран — импортируй отсюда, а не
//  хардкодь цвета по месту. Так весь интерфейс остаётся консистентным.
//
//  Палитра намеренно совпадает с темой `lux` (она дефолтная). Акцент и его
//  мягкий оттенок берём из CSS-переменных --app-accent / --app-accent-soft,
//  поэтому смена пресета акцента (Золото / Шампань / Платина) подхватывается
//  автоматически. Жёсткие нейтральные цвета (#0d0d10, #1f1f25 …) задают
//  «люкс»-каркас независимо от выбранной темы.
// ─────────────────────────────────────────────────────────────────────────

// Акцент: всегда через переменные, с золотым фолбэком.
export const ACCENT = 'var(--app-accent,#c9a24b)';
export const ACCENT_SOFT = 'var(--app-accent-soft,#e8cf8a)';

// Шрифты.
export const SERIF = "'Cormorant Garamond', Georgia, serif"; // заголовки и числа
export const SANS = "'Manrope', -apple-system, sans-serif";  // тело

// Нейтральная палитра каркаса.
export const C = {
  bg:         '#0d0d10',
  bgRadial:   'radial-gradient(120% 100% at 80% -10%,#16151b,#0d0d10 55%)',
  sidebar:    '#0a0a0d',
  text:       '#ece6d6', // основной текст / «cream»
  sub:        '#8a8a92', // подписи
  dim:        '#6a6a72', // тусклый
  dimmer:     '#5a5a62', // совсем тусклый (единицы измерения, дробь /7)
  bodyText:   '#cfcabb', // текст внутри карточек (задачи)
  noteText:   '#9a9aa2', // текст заметок
  border:     '#1f1f25', // обычная рамка
  goldBorder: '#2a2418', // золотистая рамка
  chipBorder: '#23232a', // рамка неактивного чипа
  rowBorder:  '#16161b', // тонкий внутренний разделитель строк
  trackA:     '#23232a', // дорожка прогресс-бара
  trackB:     '#2a2a30', // дорожка прогресс-бара (вариант)
  barOff:     '#2a2620', // неактивный столбик графика
  barOffHi:   '#5a4a2a', // неактивный столбик графика (высокий)
  panel:      '#101013', // фон поля поиска / инпутов в шапке
  cardGrad:    'linear-gradient(160deg,#121216,#0d0d10)',     // обычная карточка
  goldGrad:    'linear-gradient(160deg,#15140e,#0d0d10)',     // золотая карточка
  goldGrad2:   'linear-gradient(160deg,#16140d,#0d0d10)',     // золотая карточка (тёплее)
  navActive:   'linear-gradient(90deg,#1a160c,#15151a)',      // активный пункт меню
  chipActive:  '#1a160c',                                     // фон активного чипа
};

// ── Готовые объекты стилей (используй как `style={{...card}}`) ──────────────

// Надзаголовок: золотой, мелкий, капсом, с разрядкой.
export const eyebrow = {
  fontSize: 11, color: ACCENT, fontWeight: 600,
  letterSpacing: 3, textTransform: 'uppercase',
};

// Серифный титул экрана. size — крупный на десктопе, поменьше на мобайле.
export const title = (size = 44) => ({
  fontFamily: SERIF, fontSize: size, lineHeight: 1.05,
  color: 'var(--app-text,#ece6d6)', marginTop: 8,
});

// Анимированная золотая линия-разделитель (мерцающий «shimmer»).
export const rule = (mt = 18) => ({
  height: 1, marginTop: mt,
  background: `linear-gradient(90deg,${ACCENT},transparent)`,
  backgroundSize: '200% 100%',
  animation: 'shimmer 4s linear infinite',
});

// Карточки.
export const card = {
  border: `1px solid ${C.border}`, borderRadius: 18, background: C.cardGrad,
};
export const goldCard = {
  border: `1px solid ${C.goldBorder}`, borderRadius: 18, background: C.goldGrad,
};

// Чипс-пилюля. on=true → активный (золотой), иначе нейтральный.
export const chip = (on) => ({
  padding: '8px 15px', borderRadius: 18, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .2s ease',
  background: on ? C.chipActive : 'transparent',
  border: `1px solid ${on ? C.goldBorder : C.chipBorder}`,
  color: on ? ACCENT : C.sub,
});

// Золотая градиент-кнопка (тёмный текст).
export const goldBtn = {
  background: `linear-gradient(135deg,${ACCENT_SOFT},${ACCENT})`,
  color: '#0d0d10', fontWeight: 600, border: 'none',
  borderRadius: 10, cursor: 'pointer',
};

// Заливка прогресс-бара (золотой градиент). pct — 0..100.
export const barFill = (pct) => ({
  height: '100%', borderRadius: 3,
  background: `linear-gradient(90deg,${ACCENT},${ACCENT_SOFT})`,
  width: `${Math.max(0, Math.min(100, pct))}%`,
});

// Безрамочная стат-колонка (число серифом + подпись капсом).
export const statNum = { fontFamily: SERIF, fontSize: 46, lineHeight: 1, color: 'var(--app-text,#ece6d6)' };
export const statLbl = { fontSize: 9, fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: 1.5, marginTop: 6 };

// Полоска-блик «gloss»: положи как первый ребёнок в карточку с
// position:relative; overflow:hidden — будет медленно проезжать слева направо.
export const glossSheen = {
  position: 'absolute', top: 0, bottom: 0, left: 0, width: 56,
  background: 'linear-gradient(90deg,transparent,rgba(201,162,75,.15),transparent)',
  animation: 'lux-gloss 6s ease-in-out infinite', pointerEvents: 'none',
};

// ── Доп. кейфреймы, которых ещё нет в глобальном LUXE_CSS ───────────────────
// (shimmer / fadeUp / up / slide / dlflash уже определены в App.jsx — их не дублируем).
// Уважаем prefers-reduced-motion: вырубаем «фоновые» анимации.
export const LUXE_EXTRA_CSS = `
  @keyframes lux-gloss{0%{transform:translateX(-40px);opacity:0}25%{opacity:1}100%{transform:translateX(460px);opacity:0}}
  @keyframes lux-floaty{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
  @keyframes lux-glow{0%,100%{box-shadow:0 0 0 0 rgba(201,162,75,0)}50%{box-shadow:0 0 22px -4px rgba(201,162,75,.45)}}
  @media (prefers-reduced-motion: reduce){
    *{animation-duration:.001ms !important;animation-iteration-count:1 !important}
  }
`;
